import React, { useState, useEffect } from 'react';
import { LuRefreshCw } from 'react-icons/lu';
import SidebarNav from '../layout/SidebarNav';
import BottomTabBar from '../layout/BottomTabBar';
import TopBar from '../layout/TopBar';
import DocumentNavigator from './DocumentNavigator';
import DocumentPreview from './DocumentPreview';
import IntelligencePanel from './IntelligencePanel';
import DocumentChip from './DocumentChip';
import './results.css';

const ResultsPage = ({
  results,
  documents,
  fixStates,
  loadingDocs,
  onDownload,
  onAcceptFix,
  onManualRewrite,
  onNewGeneration,
  creditRegranted,
  generationFailed,
  streamActive,
  onTryAgain,
}) => {
  const [activeDocType, setActiveDocType] = useState(null);
  const [mobilePanelTab, setMobilePanelTab] = useState('preview');

  useEffect(() => {
    if (!activeDocType && results.length > 0) {
      setActiveDocType(results[0].docType);
    }
  }, [results, activeDocType]);

  const loadingDocTypes = documents
    .filter((d) => d.selected && loadingDocs[d.type] && !results.find((r) => r.docType === d.type))
    .map((d) => d.type);

  const activeResult = results.find((r) => r.docType === activeDocType) || results[0] || null;
  const activeResultIdx = activeResult ? results.indexOf(activeResult) : 0;
  const allLoading = results.length === 0 && loadingDocTypes.length > 0;

  const totalSelected = documents.filter((d) => d.selected).length;
  const totalDone = results.length;
  const isGenerating = Object.values(loadingDocs).some(Boolean);
  const progressPct = totalSelected > 0 ? Math.round((totalDone / totalSelected) * 100) : 0;
  const allFailed = !isGenerating && results.length > 0 && results.every((r) => r.failed);
  // Also covers a stream that broke before any document arrived (nothing to render).
  const failedOutcome = allFailed || (!isGenerating && results.length === 0 && generationFailed);
  // The last result line arrives before the summary line: the backend closes the run
  // out (and regrants the credit) in between. Until the stream has actually ended we
  // don't yet know whether the credit came back, so the panel's copy would change when
  // the summary lands. Hold a neutral state until then.
  const showFailedPanel = failedOutcome && !streamActive;
  const finishingFailedRun = failedOutcome && streamActive;

  const handleSelectDoc = (docType) => {
    setActiveDocType(docType);
    setMobilePanelTab('preview');
  };

  const mobileIntelTab = mobilePanelTab === 'preview' ? 'scores' : mobilePanelTab;

  return (
    <div className="results-root">
      {/* ── Sidebar (desktop) ── */}
      <SidebarNav onNewGeneration={onNewGeneration} />

      {showFailedPanel ? (
        /* ── All documents failed — full-panel state, no navigator/preview/intel ── */
        <div className="results-all-failed">
          <div className="results-all-failed-icon">
            <LuRefreshCw />
          </div>
          <h2 className="results-all-failed-title">We are sorry — something went wrong.</h2>
          {creditRegranted === true ? (
            <>
              <p className="results-all-failed-text">
                Your generation credit has been returned to your account, so this attempt did
                not use one of your credits.
              </p>
              <p className="results-all-failed-text">
                Your resume and application details are still here. You can try again now. If it
                keeps failing, contact <a href="mailto:support@zynacular.com">support@zynacular.com</a>.
              </p>
              <div className="results-all-failed-actions">
                <button type="button" className="btn-primary results-all-failed-cta" onClick={onTryAgain}>
                  Try Again
                </button>
                <a href="mailto:support@zynacular.com" className="btn-ghost results-all-failed-cta">
                  Contact Support
                </a>
              </div>
            </>
          ) : (
            <>
              <p className="results-all-failed-text">
                We apologize for the inconvenience. Please contact{' '}
                <a href="mailto:support@zynacular.com">support@zynacular.com</a> so our team can
                review the issue and restore your generation credit.
              </p>
              <p className="results-all-failed-text">
                You will need to start a new generation once this is resolved.
              </p>
              <div className="results-all-failed-actions">
                <a href="mailto:support@zynacular.com" className="btn-primary results-all-failed-cta">
                  Contact Support
                </a>
              </div>
            </>
          )}
        </div>
      ) : finishingFailedRun ? (
        /* ── Every document failed but the stream hasn't ended — neutral until it does ── */
        <div className="results-all-loading">
          <div className="results-all-loading-spinner" />
          <span className="results-all-loading-text">Generating your documents…</span>
        </div>
      ) : (
        <>
          {/* ── Document Navigator (desktop) ── */}
          <DocumentNavigator
            results={results}
            loadingDocTypes={loadingDocTypes}
            activeDocType={activeDocType || activeResult?.docType}
            onSelect={handleSelectDoc}
            isGenerating={isGenerating}
            totalSelected={totalSelected}
            totalDone={totalDone}
            progressPct={progressPct}
            onNewGeneration={onNewGeneration}
          />

          {/* ── Center Column ── */}
          <div className="results-center-col">
            {/* Generation progress bar (desktop top of center) */}
            {isGenerating && totalSelected > 1 && (
              <div className="results-gen-progress">
                <div className="results-gen-progress-text">
                  Generating {totalDone + loadingDocTypes.length} of {totalSelected} documents…
                </div>
                <div className="results-gen-progress-track">
                  <div
                    className="results-gen-progress-fill"
                    style={{ width: `${progressPct}%` }}
                  />
                </div>
              </div>
            )}

            {/* Mobile: top bar */}
            <TopBar docType={activeResult?.docType} onBack={onNewGeneration} />

            {/* Mobile: document chip strip */}
            <div className="results-chip-strip">
              {results.map((r) => (
                <DocumentChip
                  key={r.docType}
                  result={r}
                  isActive={r.docType === (activeDocType || activeResult?.docType)}
                  onClick={() => handleSelectDoc(r.docType)}
                />
              ))}
            </div>

            {/* Mobile: preview / impact / scores tabs */}
            <div className="results-mobile-tabs">
              {['preview', 'impact', 'scores'].map((tab) => (
                <button
                  key={tab}
                  className={`mobile-view-tab ${mobilePanelTab === tab ? 'mobile-view-tab--active' : ''}`}
                  onClick={() => setMobilePanelTab(tab)}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                </button>
              ))}
            </div>

            {/* Document Preview */}
            <div
              className={`results-preview-area ${
                mobilePanelTab !== 'preview' ? 'results-preview-area--hidden' : ''
              }`}
            >
              {allLoading ? (
                <div className="results-all-loading" style={{ flex: 1 }}>
                  <div className="results-all-loading-spinner" />
                  <span className="results-all-loading-text">Generating your documents…</span>
                </div>
              ) : activeResult ? (
                <DocumentPreview
                  key={activeResult.docType}
                  result={activeResult}
                  isLoading={loadingDocs[activeResult.docType]}
                  onDownload={onDownload}
                  onNewGeneration={onNewGeneration}
                />
              ) : null}
            </div>

            {/* Mobile: intelligence panel */}
            <div
              className={`results-mobile-intel ${
                mobilePanelTab !== 'preview' ? 'results-mobile-intel--visible' : ''
              }`}
            >
              {activeResult && (
                <IntelligencePanel
                  result={activeResult}
                  resultIdx={activeResultIdx}
                  fixStates={fixStates}
                  onAcceptFix={onAcceptFix}
                  onManualRewrite={onManualRewrite}
                  forcedTab={mobileIntelTab}
                />
              )}
            </div>
          </div>

          {/* ── Intelligence Panel (desktop right) ── */}
          <div className="results-intel-desktop">
            <IntelligencePanel
              result={activeResult}
              resultIdx={activeResultIdx}
              fixStates={fixStates}
              onAcceptFix={onAcceptFix}
              onManualRewrite={onManualRewrite}
            />
          </div>
        </>
      )}

      {/* ── Bottom Tab Bar (mobile) ── */}
      <BottomTabBar onNewGeneration={onNewGeneration} />
    </div>
  );
};

export default ResultsPage;
